/**
 * Fetches the admin `/users` and `/tracks` lists once and exposes them as the
 * id → label lookup the approvals pages render beside each id.
 *
 * Failure is deliberately silent: the labels are garnish over ids that are
 * already on screen, so a failed lookup must not blank the queue or raise a
 * banner over a page whose own data loaded fine.
 */
import { useEffect, useState } from 'react';
import type {
  TfTrackRow,
  UserListResponse,
} from '@verybigthings/trackframe-shared/schemas';
import { hub } from './hub-client';
import {
  buildPrincipalLabels,
  EMPTY_PRINCIPAL_LABELS,
  type PrincipalLabels,
} from './principal-labels';

export function usePrincipalLabels(): PrincipalLabels {
  const [labels, setLabels] = useState<PrincipalLabels>(EMPTY_PRINCIPAL_LABELS);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [users, tracks] = await Promise.all([
          hub.usersList() as Promise<UserListResponse>,
          hub.tracksList() as Promise<TfTrackRow[]>,
        ]);
        if (cancelled) return;
        setLabels(buildPrincipalLabels(users.users, tracks));
      } catch {
        // Ids keep rendering unlabelled — see the module docblock.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return labels;
}
